import React, { useCallback, useRef } from "react";
import cx from "classnames";
import ButtonIconV2 from "./ButtonIconV2";
import { DownloadLine } from "./IconV2/react";
import TooltipV2 from "./TooltipV2";
import t from "./i18n";
import download from "downloadjs";
import { HIDE_ON_EXPORT } from "./lib/downloadPage";
import useToasts from "./Toast/useToasts";
import { spacings } from "./Theme";
import { css } from "@linaria/core";
import { domToPng } from "modern-screenshot";

export const exportWidgetTarget = "export-widget-target";
export const HIDE_ON_WIDGET_EXPORT = "hide-on-widget-export";

const exportWidgetButton = css`
  box-sizing: border-box;
  width: ${spacings.xl};
  height: ${spacings.xl};
  @media print {
    display: none;
  }
  > * {
    pointer-events: none;
  }
`;

export function ExportWidgetButton({ fileName }: { fileName: string }) {
  const root = useRef<HTMLButtonElement>(null);
  const { addToast } = useToasts();
  const handleDownload = useCallback(async () => {
    const target = root.current?.closest(`.${exportWidgetTarget}`) as HTMLElement;
    if (!target) return;

    const imgs = Array.from(target.querySelectorAll("img"));
    const previousSrcs = imgs.map(i => i.src);

    function transformToData(img: HTMLImageElement): Promise<void> {
      return fetch(img.src, { credentials: "include" })
        .then(res => {
          if (!res.ok) throw new Error("error loading image");
          return res.blob();
        })
        .then(
          blob =>
            new Promise(resolve => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            }),
        )
        .then(data => {
          if (typeof data === "string") {
            img.src = data;
          }
        })
        .catch(err => {
          addToast("error", err instanceof Error ? err.message : `${err}`);
        });
    }

    await Promise.all(imgs.map(transformToData)).then(() => {
      /** @note wait next task */
      setTimeout(() => {
        domToPng(target, {
          filter: node =>
            node instanceof HTMLElement
              ? !node.classList.contains(HIDE_ON_EXPORT) &&
                !node.classList.contains(HIDE_ON_WIDGET_EXPORT)
              : true,
        })
          .then(blob => download(blob, fileName))
          .then(() => addToast("success", t("chart.download.success", fileName)))
          .then(() => {
            for (let i = 0; i < imgs.length; i++) {
              const img = imgs[i];
              const src = previousSrcs[i];
              if (img && src) {
                img.src = src;
              }
            }
          })
          .catch(() => addToast("error", t("chart.download.error")));
      });
    });
  }, [addToast, fileName]);

  return (
    <TooltipV2 overlay={t("chart.download.png")}>
      <ButtonIconV2
        ref={root}
        onClick={handleDownload}
        className={cx(exportWidgetButton, HIDE_ON_WIDGET_EXPORT)}
      >
        <DownloadLine size="m" />
      </ButtonIconV2>
    </TooltipV2>
  );
}
